import React, { useRef, useState } from 'react';
import ObjectTopBar from '../components/topbar/ObjectTopBar';
import useDocumentTitle from '../../shared/hooks/useDocumentTitle';
import LoadingView from '../../shared/components/LoadingView';
import useMediaLibrary from '../hooks/useMediaLibrary';
import MediaLibraryPanel from '../components/media-library/MediaLibraryPanel';

export default function OrganizationMediaLibraryPage() {
  useDocumentTitle('Media library – Organization settings – Maintella');
  const fileInputRef = useRef(null);
  const [uploadNotice, setUploadNotice] = useState('');
  const {
    items,
    loading,
    error,
    uploading,
    uploadFile,
    deleteItem,
    fetchDownloadUrl,
    fetchUsages,
  } = useMediaLibrary();

  // Uploads run one at a time rather than in parallel: the hook prepares and completes each
  // upload against the API, and a dropped folder of twenty would otherwise open twenty at once.
  const uploadFiles = async (files) => {
    setUploadNotice('');
    let reused = 0;

    for (const file of files) {
      try {
        const result = await uploadFile(file);
        if (result?.deduplicated) reused += 1;
      } catch {
        // error surfaced via hook; keep going so one bad file does not stop the rest
      }
    }

    if (reused > 0) {
      setUploadNotice(reused === 1
        ? 'One uploaded file matched an existing library image; the existing item was reused.'
        : `${reused} uploaded files matched existing library images; the existing items were reused.`);
    }
  };

  const handleFileChange = async (event) => {
    const files = [...(event.target.files || [])];
    event.target.value = '';
    if (files.length) await uploadFiles(files);
  };

  return (
    <>
      <ObjectTopBar
        title="Media library"
        primaryBtn={{
          btnText: uploading ? 'Uploading…' : 'Upload image',
          btnIcon: 'bi-upload',
          variant: 'primary',
          onClick: () => fileInputRef.current?.click(),
          disabled: uploading,
        }}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="d-none"
        onChange={handleFileChange}
      />

      <div className="container py-4">
        <div className="mb-4">
          <h6 className="text-muted fw-normal mb-1">Workflow instruction images</h6>
          <p className="text-muted small mb-0">
            Images for workflow instructions and photo-step reference graphics, shared across your
            organization&apos;s workflows. Asset files and field capture photos are managed separately.
          </p>
        </div>

        {loading && items.length === 0 ? (
          <LoadingView
            symbolClass="bi-images"
            title="Loading media library"
            subtitle="Fetching images for your organization."
            className="py-5"
          />
        ) : (
          <>
            {(error || uploadNotice) && (
              <div
                className={`alert mb-3 ${error ? 'alert-danger' : 'alert-info'}`}
                role="alert"
              >
                {error && <i className="bi bi-exclamation-triangle me-2" />}
                {error || uploadNotice}
              </div>
            )}
            <MediaLibraryPanel
              items={items}
              loading={loading}
              error={error}
              fetchDownloadUrl={fetchDownloadUrl}
              fetchUsages={fetchUsages}
              onDelete={deleteItem}
              onPickFiles={() => fileInputRef.current?.click()}
              onUpload={uploadFiles}
              uploading={uploading}
            />
          </>
        )}
      </div>
    </>
  );
}
